import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import type { SpassuCodeApi } from './SpassuCodeApiContract';
import { ApiTransport } from './ApiTransport';
import { SkillsClient } from './SkillsClient';
import { AccessClient } from './AccessClient';
import { AuditClient } from './AuditClient';
import { ComplianceClient } from './ComplianceClient';
import { ActivitiesClient } from './ActivitiesClient';
import { ScmClient } from './ScmClient';
import { BoardsClient } from './BoardsClient';

export type { SpassuCodeApi } from './SpassuCodeApiContract';

export const spassuCodeApiRef = createApiRef<SpassuCodeApi>({
  id: 'plugin.spassu-code.api',
});

export class SpassuCodeClient implements SpassuCodeApi {
  private readonly skills: SkillsClient;
  private readonly access: AccessClient;
  private readonly audit: AuditClient;
  private readonly compliance: ComplianceClient;
  private readonly activities: ActivitiesClient;
  private readonly scm: ScmClient;
  private readonly boards: BoardsClient;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    const transport = new ApiTransport(discoveryApi, fetchApi);
    this.skills = new SkillsClient(transport);
    this.access = new AccessClient(transport);
    this.audit = new AuditClient(transport);
    this.compliance = new ComplianceClient(transport);
    this.activities = new ActivitiesClient(transport);
    this.scm = new ScmClient(transport);
    this.boards = new BoardsClient(transport);
  }

  async listProjects(...args: Parameters<SpassuCodeApi['listProjects']>) {
    return this.scm.listProjects(...args);
  }

  async listScmProviders(
    ...args: Parameters<SpassuCodeApi['listScmProviders']>
  ) {
    return this.scm.listScmProviders(...args);
  }

  async listScmOwners(...args: Parameters<SpassuCodeApi['listScmOwners']>) {
    return this.scm.listScmOwners(...args);
  }

  async listManagedProjects(
    ...args: Parameters<SpassuCodeApi['listManagedProjects']>
  ) {
    return this.scm.listManagedProjects(...args);
  }

  async listAllRepositoriesForOwner(
    ...args: Parameters<SpassuCodeApi['listAllRepositoriesForOwner']>
  ) {
    return this.scm.listAllRepositoriesForOwner(...args);
  }

  async listScmProjects(...args: Parameters<SpassuCodeApi['listScmProjects']>) {
    return this.scm.listScmProjects(...args);
  }

  async listScmRepositories(
    ...args: Parameters<SpassuCodeApi['listScmRepositories']>
  ) {
    return this.scm.listScmRepositories(...args);
  }

  async listScmBranches(...args: Parameters<SpassuCodeApi['listScmBranches']>) {
    return this.scm.listScmBranches(...args);
  }

  async searchIssues(...args: Parameters<SpassuCodeApi['searchIssues']>) {
    return this.boards.searchIssues(...args);
  }

  async getIssue(...args: Parameters<SpassuCodeApi['getIssue']>) {
    return this.boards.getIssue(...args);
  }

  async listRepositories(
    ...args: Parameters<SpassuCodeApi['listRepositories']>
  ) {
    return this.scm.listRepositories(...args);
  }

  async listBranches(...args: Parameters<SpassuCodeApi['listBranches']>) {
    return this.scm.listBranches(...args);
  }

  async createActivity(...args: Parameters<SpassuCodeApi['createActivity']>) {
    return this.activities.createActivity(...args);
  }

  async listActivities(...args: Parameters<SpassuCodeApi['listActivities']>) {
    return this.activities.listActivities(...args);
  }

  async getActivityFilterOptions(
    ...args: Parameters<SpassuCodeApi['getActivityFilterOptions']>
  ) {
    return this.activities.getActivityFilterOptions(...args);
  }

  async requestActivityDeletionBatch(
    ...args: Parameters<SpassuCodeApi['requestActivityDeletionBatch']>
  ) {
    return this.activities.requestActivityDeletionBatch(...args);
  }

  async getActivityDeletionBatch(
    ...args: Parameters<SpassuCodeApi['getActivityDeletionBatch']>
  ) {
    return this.activities.getActivityDeletionBatch(...args);
  }

  async overrideActivityDeletionCleanup(
    ...args: Parameters<SpassuCodeApi['overrideActivityDeletionCleanup']>
  ) {
    return this.activities.overrideActivityDeletionCleanup(...args);
  }

  async getActivity(...args: Parameters<SpassuCodeApi['getActivity']>) {
    return this.activities.getActivity(...args);
  }

  async listSkills(...args: Parameters<SpassuCodeApi['listSkills']>) {
    return this.skills.listSkills(...args);
  }

  async getSkill(...args: Parameters<SpassuCodeApi['getSkill']>) {
    return this.skills.getSkill(...args);
  }

  async createSkill(...args: Parameters<SpassuCodeApi['createSkill']>) {
    return this.skills.createSkill(...args);
  }

  async updateSkill(...args: Parameters<SpassuCodeApi['updateSkill']>) {
    return this.skills.updateSkill(...args);
  }

  async signSkill(...args: Parameters<SpassuCodeApi['signSkill']>) {
    return this.skills.signSkill(...args);
  }

  async submitSkillForReview(
    ...args: Parameters<SpassuCodeApi['submitSkillForReview']>
  ) {
    return this.skills.submitSkillForReview(...args);
  }

  async approveSkill(...args: Parameters<SpassuCodeApi['approveSkill']>) {
    return this.skills.approveSkill(...args);
  }

  async submitCommunitySkill(
    ...args: Parameters<SpassuCodeApi['submitCommunitySkill']>
  ) {
    return this.skills.submitCommunitySkill(...args);
  }

  async getMyRoles(...args: Parameters<SpassuCodeApi['getMyRoles']>) {
    return this.access.getMyRoles(...args);
  }

  async getProjectMembers(
    ...args: Parameters<SpassuCodeApi['getProjectMembers']>
  ) {
    return this.access.getProjectMembers(...args);
  }

  async getAssignableRoles(
    ...args: Parameters<SpassuCodeApi['getAssignableRoles']>
  ) {
    return this.access.getAssignableRoles(...args);
  }

  async addProjectMember(
    ...args: Parameters<SpassuCodeApi['addProjectMember']>
  ) {
    return this.access.addProjectMember(...args);
  }

  async removeProjectMember(
    ...args: Parameters<SpassuCodeApi['removeProjectMember']>
  ) {
    return this.access.removeProjectMember(...args);
  }

  async updateProjectMemberRole(
    ...args: Parameters<SpassuCodeApi['updateProjectMemberRole']>
  ) {
    return this.access.updateProjectMemberRole(...args);
  }

  async getAuditLog(...args: Parameters<SpassuCodeApi['getAuditLog']>) {
    return this.audit.getAuditLog(...args);
  }

  async renewProjectMember(
    ...args: Parameters<SpassuCodeApi['renewProjectMember']>
  ) {
    return this.access.renewProjectMember(...args);
  }

  async exportAuditLogCsv(
    ...args: Parameters<SpassuCodeApi['exportAuditLogCsv']>
  ) {
    return this.audit.exportAuditLogCsv(...args);
  }

  async invalidateRbacCache(
    ...args: Parameters<SpassuCodeApi['invalidateRbacCache']>
  ) {
    return this.access.invalidateRbacCache(...args);
  }

  async getProjectPolicies(
    ...args: Parameters<SpassuCodeApi['getProjectPolicies']>
  ) {
    return this.compliance.getProjectPolicies(...args);
  }

  async setProjectPolicy(
    ...args: Parameters<SpassuCodeApi['setProjectPolicy']>
  ) {
    return this.compliance.setProjectPolicy(...args);
  }

  async getProjectCompliance(
    ...args: Parameters<SpassuCodeApi['getProjectCompliance']>
  ) {
    return this.compliance.getProjectCompliance(...args);
  }

  async finalizeActivity(
    ...args: Parameters<SpassuCodeApi['finalizeActivity']>
  ) {
    return this.activities.finalizeActivity(...args);
  }

  async listWorkItemTypes(
    ...args: Parameters<SpassuCodeApi['listWorkItemTypes']>
  ) {
    return this.boards.listWorkItemTypes(...args);
  }

  async searchWorkItems(...args: Parameters<SpassuCodeApi['searchWorkItems']>) {
    return this.boards.searchWorkItems(...args);
  }

  async getWorkItem(...args: Parameters<SpassuCodeApi['getWorkItem']>) {
    return this.boards.getWorkItem(...args);
  }
}
